import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
} from '@mui/material';
import {
  CheckCircleOutline as CheckIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useAppStore } from '@/store';
import PermissionsList, { PermissionItem } from '@/components/PermissionsList';
import { formatNewChatTitle } from '@/utils/dateUtils';
import DialogWrapper from '@/components/DialogWrapper';
import CloseIcon from '@mui/icons-material/Close';
import { useCompleteOnboardingMutation } from '@/features/userSettings/hooks/useUserSettingsMutations';
import { useCreateConversationMutation } from '@/features/conversations/hooks/useConversationMutations';
import { listConversations, updateConversation } from '@/api/conversations';
import {
  usePermissionRequestsQuery,
  useAccessiblePermissionsQuery,
  useRequestablePermissionsQuery,
} from '@/features/permissionRequests/hooks/usePermissionRequestsQuery';
import { useCreatePermissionRequestBatchMutation } from '@/features/permissionRequests/hooks/usePermissionRequestsMutations';

const Onboarding: React.FC = () => {
  const { setUserSettings, setConversationList, setSelectedConversation } = useAppStore();
  const user = useAppStore(s => s.user);
  const userSettings = useAppStore(s => s.userSettings);
  const displayName = user?.name || userSettings?.name || 'there';
  
  // Mutation hooks
  const completeOnboardingMutation = useCompleteOnboardingMutation();
  const createConversationMutation = useCreateConversationMutation();
  const createPermissionRequestBatchMutation = useCreatePermissionRequestBatchMutation();
  
  // Query hooks - enabled when dialog opens
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const { data: accessiblePermissions } = useAccessiblePermissionsQuery(permissionDialogOpen);
  const { data: requestablePermissions } = useRequestablePermissionsQuery(permissionDialogOpen);
  
  // Local state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState<number>(0);

  // Permissions state for page 2
  const [availableModels, setAvailableModels] = useState<PermissionItem[]>([]);
  const [availableAgents, setAvailableAgents] = useState<PermissionItem[]>([]);
  const [availableFeatures, setAvailableFeatures] = useState<PermissionItem[]>([]);
  const [allModels, setAllModels] = useState<PermissionItem[]>([]);
  const [allAgents, setAllAgents] = useState<PermissionItem[]>([]);
  const [allFeatures, setAllFeatures] = useState<PermissionItem[]>([]);
  const [requestedModelIds, setRequestedModelIds] = useState<string[]>([]);
  const [requestedAgentIds, setRequestedAgentIds] = useState<string[]>([]);
  const [requestedFeatureIds, setRequestedFeatureIds] = useState<string[]>([]);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const { data: pendingPermissionRequestsData } = usePermissionRequestsQuery('pending', false, requestDialogOpen);

  const pendingPermissions = useMemo(() => {
    if (!pendingPermissionRequestsData) {
      return [] as Array<{ permission?: string }>;
    }

    if (Array.isArray((pendingPermissionRequestsData as any).requests)) {
      return (pendingPermissionRequestsData as any).requests as Array<{ permission?: string }>;
    }

    if (Array.isArray(pendingPermissionRequestsData as any)) {
      return pendingPermissionRequestsData as unknown as Array<{ permission?: string }>;
    }

    return [] as Array<{ permission?: string }>;
  }, [pendingPermissionRequestsData]);

  const pendingModelIds = useMemo(() => (
    pendingPermissions
      .filter((req) => typeof req.permission === 'string' && req.permission.startsWith('model:'))
      .map((req) => (req.permission as string).split(':', 2)[1])
      .filter(Boolean)
  ), [pendingPermissions]);

  const pendingAgentIds = useMemo(() => (
    pendingPermissions
      .filter((req) => typeof req.permission === 'string' && req.permission.startsWith('agent:'))
      .map((req) => (req.permission as string).split(':', 2)[1])
      .filter(Boolean)
  ), [pendingPermissions]);

  const pendingFeatureIds = useMemo(() => (
    pendingPermissions
      .filter((req) => typeof req.permission === 'string' && req.permission.startsWith('feature:'))
      .map((req) => (req.permission as string).split(':', 2)[1])
      .filter(Boolean)
  ), [pendingPermissions]);

  const handleToggleModelRequest = (id: string) => setRequestedModelIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const handleToggleAgentRequest = (id: string) => setRequestedAgentIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const handleToggleFeatureRequest = (id: string) => setRequestedFeatureIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const openRequestDialog = async () => {
    setPermissionDialogOpen(true);
    setRequestDialogOpen(true);
  };

  // Populate state when permissions data loads
  useEffect(() => {
    if (accessiblePermissions && requestablePermissions) {
      setAvailableModels(accessiblePermissions.models as any);
      setAvailableAgents(accessiblePermissions.agents as any);
      setAvailableFeatures(accessiblePermissions.features as any);
      setAllModels(requestablePermissions.models as any);
      setAllAgents(requestablePermissions.agents as any);
      setAllFeatures(requestablePermissions.features as any);
    }
  }, [accessiblePermissions, requestablePermissions]);

  const submitPermissionRequests = async () => {
    if (submitInFlightRef.current) {
      return;
    }

    try {
      submitInFlightRef.current = true;
      setSubmitting(true);
      const requestedPermissions = [
        ...requestedModelIds.map(id => `model:${id}`),
        ...requestedAgentIds.map(id => `agent:${id}`),
        ...requestedFeatureIds.map(id => `feature:${id}`),
      ];
      if (requestedPermissions.length === 0) return;
      await createPermissionRequestBatchMutation.mutateAsync({
        requested_permissions: requestedPermissions,
      });
      setRequestDialogOpen(false);
      setRequestedModelIds([]);
      setRequestedAgentIds([]);
      setRequestedFeatureIds([]);
      setSnackbarMessage('Your permission request has been sent.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (e) {
      console.error('Failed to submit onboarding permission request', e);
      setSnackbarMessage('Failed to send permission request.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally { 
      submitInFlightRef.current = false;
      setSubmitting(false); 
    }
  };

  useEffect(() => {
    if (!requestDialogOpen) {
      return;
    }

    setRequestedModelIds((prev) => prev.filter((id) => !pendingModelIds.includes(id)));
    setRequestedAgentIds((prev) => prev.filter((id) => !pendingAgentIds.includes(id)));
    setRequestedFeatureIds((prev) => prev.filter((id) => !pendingFeatureIds.includes(id)));
  }, [requestDialogOpen, pendingModelIds, pendingAgentIds, pendingFeatureIds]);

  // Filter non-accessible permissions (exclude already accessible and implicit)
  const nonAccessibleModels = allModels.filter(m => 
    !availableModels.some(am => am.id === m.id) && !m.is_implicit
  );
  const nonAccessibleAgents = allAgents.filter(a => 
    !availableAgents.some(aa => aa.id === a.id) && !a.is_implicit
  );
  const nonAccessibleFeatures = allFeatures.filter(f => 
    !availableFeatures.some(af => af.id === f.id) && !f.is_implicit
  );

  const handleCompleteOnboarding = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Complete onboarding
      const updatedSettings = await completeOnboardingMutation.mutateAsync();

      const conversations = await listConversations();

      // Reuse existing empty conversation if available.
      const emptyConversation = conversations
        .filter((conv) => conv.message_count === 0 || conv.message_count === undefined)
        .sort((a, b) => {
          const aTime = new Date(a.updated_date || a.created_date).getTime();
          const bTime = new Date(b.updated_date || b.created_date).getTime();
          return bTime - aTime;
        })[0];

      let targetConversationId = '';
      if (emptyConversation) {
        const updatedConversation = await updateConversation(emptyConversation.id, {
          title: formatNewChatTitle(),
          updated_date: new Date().toISOString(),
          agent_key: 'default',
        });
        targetConversationId = updatedConversation.id;
      } else {
        const newConversation = await createConversationMutation.mutateAsync({
          title: formatNewChatTitle(),
        });
        targetConversationId = newConversation.id;
      }

      const refreshedConversations = await listConversations();
      setConversationList(refreshedConversations);

      const targetConversation = refreshedConversations.find(
        (conv) => conv.id === targetConversationId
      );
      if (targetConversation) {
        setSelectedConversation(targetConversation);
      }

      setUserSettings(updatedSettings);
      
      // No need to navigate - App.tsx will automatically render the main app
      // when userSettings.onboarding_completed becomes true
    } catch (err: any) {
      console.error('Failed to complete onboarding:', err);
      setError(err.response?.data?.detail || 'Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: 4,
        px: 3,
        overflowY: 'auto',
      }}
    >
      <Container maxWidth="md">
        <Card elevation={3}>
          <CardContent sx={{ p: 4 }}>
            {/* Welcome Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h3" gutterBottom color="primary">
                {`Hi ${displayName}!`} 🎉
              </Typography>
              <Typography variant="h4" gutterBottom color="primary">
                Welcome to <strong>Bolt</strong>, Bossard AI Platform
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Let's get you started with your new account
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Your Account Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h5" color="primary">
                  Your Account
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Your account has been created with the following default settings:
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="User Role"
                    secondary="You've been assigned the 'User' role with standard access"
                  />
                </ListItem>
                { /* Manager display: show manager name above Business Unit */ }
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Manager"
                    secondary={userSettings?.manager_name || 'Unassigned'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Business Unit"
                    secondary={
                      userSettings?.business_unit && userSettings.business_unit !== 'Unassigned'
                        ? userSettings.business_unit
                        : 'Unassigned - Your manager or administrator can update this later'
                    }
                  />
                </ListItem>
              </List>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Permissions Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h5" color="primary">
                  Permissions & Access
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Your access to AI models and features is managed through permissions:
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="info" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Default Access"
                    secondary="You have access to standard features configured for all users"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="info" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Request Additional Permissions"
                    secondary="Need access to advanced AI models? You can request permissions at any time"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="info" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Approval Process"
                    secondary="Your manager or administrator will review and approve your permission requests"
                  />
                </ListItem>
              </List>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Next Steps Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssignmentIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h5" color="primary">
                  What's Next?
                </Typography>
              </Box>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Explore the Chat Interface"
                    secondary="Start conversations with available AI agents and models"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Use Translation Features"
                    secondary="Translate text and documents in multiple languages"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Customize Your Settings"
                    secondary="Adjust your theme preference and other settings in the user menu"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Request Permissions"
                    secondary="Request access to advanced AI models and features as needed"
                  />
                </ListItem>
              </List>
            </Box>

            {/* Error Display */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

              {/* Page content switch: page 0 informational, page 1 permissions request */}
              {pageIndex === 1 && (
                <Box sx={{ mb: 3 }}>
                  <PermissionsList
                    title="Model Permissions"
                    available={availableModels}
                    allItems={allModels}
                    pendingIds={pendingModelIds}
                    requestedIds={requestedModelIds}
                    onToggleRequest={handleToggleModelRequest}
                  />

                  <PermissionsList
                    title="Agent Permissions"
                    available={availableAgents}
                    allItems={allAgents}
                    pendingIds={pendingAgentIds}
                    requestedIds={requestedAgentIds}
                    onToggleRequest={handleToggleAgentRequest}
                  />

                  <PermissionsList
                    title="Feature Permissions"
                    available={availableFeatures}
                    allItems={allFeatures}
                    pendingIds={pendingFeatureIds}
                    requestedIds={requestedFeatureIds}
                    onToggleRequest={handleToggleFeatureRequest}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button variant="outlined" onClick={openRequestDialog}>Request Selected</Button>
                  </Box>
                </Box>
              )}

              {/* Complete Button */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                {pageIndex === 0 ? (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleCompleteOnboarding}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} sx={{ color: '#fff !important' }} /> : <CheckIcon />}
                    sx={{
                      px: 6,
                      py: 1.5,
                      fontSize: '1.1rem',
                      color: '#fff !important',
                      backgroundColor: 'primary.main',
                      '&:hover': {
                        color: '#fff !important',
                        backgroundColor: 'primary.dark',
                      },
                      '& .MuiButton-startIcon': {
                        color: '#fff !important',
                      },
                    }}
                  >
                    {loading ? 'Setting up...' : "Let's Get Started!"}
                  </Button>
                ) : (
                  <>
                    <Button variant="outlined" onClick={() => setPageIndex(0)}>Back</Button>
                    <Button variant="contained" onClick={handleCompleteOnboarding}>Finish</Button>
                  </>
                )}
              </Box>
              {/* <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{ mt: 2 }}
              >
                You can always access help and documentation from the user menu
              </Typography> */}
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
    {/* Request Permissions Dialog */}
    <DialogWrapper open={requestDialogOpen} onClose={() => setRequestDialogOpen(false)} maxWidth="md" fullWidth>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Request Permissions</Typography>
          <IconButton onClick={() => setRequestDialogOpen(false)}><CloseIcon /></IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>Select the permissions you would like to request. Your manager will be notified.</Typography>

        {/* Permissions selection lists (like User Settings dialog) */}
        <PermissionsList
          title="Model Permissions"
          available={availableModels}
          allItems={nonAccessibleModels.concat(availableModels)}
          pendingIds={pendingModelIds}
          requestedIds={requestedModelIds}
          onToggleRequest={handleToggleModelRequest}
          compact
        />

        <PermissionsList
          title="Agent Permissions"
          available={availableAgents}
          allItems={nonAccessibleAgents.concat(availableAgents)}
          pendingIds={pendingAgentIds}
          requestedIds={requestedAgentIds}
          onToggleRequest={handleToggleAgentRequest}
          compact
        />

        <PermissionsList
          title="Feature Permissions"
          available={availableFeatures}
          allItems={nonAccessibleFeatures.concat(availableFeatures)}
          pendingIds={pendingFeatureIds}
          requestedIds={requestedFeatureIds}
          onToggleRequest={handleToggleFeatureRequest}
          compact
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button variant="outlined" onClick={() => setRequestDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitPermissionRequests} disabled={submitting || (requestedModelIds.length === 0 && requestedAgentIds.length === 0 && requestedFeatureIds.length === 0)}>
            {submitting ? 'Submitting...' : 'Send Request'}
          </Button>
        </Box>
      </Box>
    </DialogWrapper>
    <Snackbar
      open={snackbarOpen}
      autoHideDuration={6000}
      onClose={() => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
        {snackbarMessage}
      </Alert>
    </Snackbar>
    </>
  );
};

export default Onboarding;
